/**
 * 전화번호 포맷팅 및 마스킹 유틸리티
 */

/**
 * 이름을 마스킹 처리 (첫글자와 마지막 글자만 남기고 가운데 마스킹)
 * @param {string} name - 원본 이름
 * @returns {string} 마스킹된 이름
 */
export function maskName(name) {
  if (!name) return '-';

  const trimmedName = name.trim();
  const length = trimmedName.length;

  if (length === 0) return '-';
  if (length === 1) return trimmedName;
  if (length === 2) return trimmedName[0] + '*';

  // 3글자 이상인 경우: 첫글자 + 가운데 마스킹 + 마지막 글자
  const first = trimmedName[0];
  const last = trimmedName[length - 1];
  const middleLength = length - 2;
  const maskedMiddle = '*'.repeat(middleLength);

  return first + maskedMiddle + last;
}

/**
 * 전화번호를 010-0000-0000 형식으로 포맷팅하고 가운데 4자리를 마스킹 처리
 * @param {string} phoneNumber - 원본 전화번호
 * @returns {string} 포맷팅 및 마스킹된 전화번호
 */
export function formatAndMaskPhone(phoneNumber) {
  if (!phoneNumber) return '-';

  // 숫자만 추출
  const cleaned = phoneNumber.replace(/\D/g, '');

  // 11자리 휴대폰 번호 (010-0000-0000)
  if (cleaned.length === 11 && cleaned.startsWith('010')) {
    const first = cleaned.slice(0, 3);
    const middle = '****'; // 가운데 4자리 마스킹
    const last = cleaned.slice(7, 11);
    return `${first}-${middle}-${last}`;
  }

  // 10자리 휴대폰 번호 (011-000-0000, 016-000-0000 등)
  if (cleaned.length === 10 && /^01[1-9]/.test(cleaned)) {
    const first = cleaned.slice(0, 3);
    const middle = '***'; // 가운데 3자리 마스킹
    const last = cleaned.slice(6, 10);
    return `${first}-${middle}-${last}`;
  }

  // 일반 전화번호 (02-0000-0000, 031-000-0000 등)
  if (cleaned.length >= 9) {
    // 지역번호 추출
    let areaCode = '';
    let remainingNumber = '';

    if (cleaned.startsWith('02')) {
      areaCode = '02';
      remainingNumber = cleaned.slice(2);
    } else if (cleaned.startsWith('0')) {
      areaCode = cleaned.slice(0, 3);
      remainingNumber = cleaned.slice(3);
    } else {
      // 0으로 시작하지 않는 경우 원본 반환
      return phoneNumber;
    }

    // 나머지 번호 처리
    if (remainingNumber.length === 7) {
      // 7자리인 경우 (000-0000)
      const middle = '***';
      const last = remainingNumber.slice(3, 7);
      return `${areaCode}-${middle}-${last}`;
    } else if (remainingNumber.length === 8) {
      // 8자리인 경우 (0000-0000)
      const middle = '****';
      const last = remainingNumber.slice(4, 8);
      return `${areaCode}-${middle}-${last}`;
    }
  }

  // 포맷팅할 수 없는 경우 원본 반환
  return phoneNumber;
}

/**
 * 전화번호를 010-0000-0000 형식으로 포맷팅 (마스킹 없이)
 * @param {string} phoneNumber - 원본 전화번호
 * @returns {string} 포맷팅된 전화번호
 */
export function formatPhone(phoneNumber) {
  if (!phoneNumber) return '-';

  // 숫자만 추출
  const cleaned = phoneNumber.replace(/\D/g, '');

  // 11자리 휴대폰 번호 (010-0000-0000)
  if (cleaned.length === 11 && cleaned.startsWith('010')) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 7)}-${cleaned.slice(7, 11)}`;
  }

  // 10자리 휴대폰 번호 (011-000-0000, 016-000-0000 등)
  if (cleaned.length === 10 && /^01[1-9]/.test(cleaned)) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  }

  // 일반 전화번호
  if (cleaned.length >= 9) {
    let areaCode = '';
    let remainingNumber = '';

    if (cleaned.startsWith('02')) {
      areaCode = '02';
      remainingNumber = cleaned.slice(2);
    } else if (cleaned.startsWith('0')) {
      areaCode = cleaned.slice(0, 3);
      remainingNumber = cleaned.slice(3);
    } else {
      return phoneNumber;
    }

    if (remainingNumber.length === 7) {
      return `${areaCode}-${remainingNumber.slice(0, 3)}-${remainingNumber.slice(3, 7)}`;
    } else if (remainingNumber.length === 8) {
      return `${areaCode}-${remainingNumber.slice(0, 4)}-${remainingNumber.slice(4, 8)}`;
    }
  }

  return phoneNumber;
}